<template>
  <div class="n-input n-input-address">
    <n-popup :config="config">
      <template #reference>
        <div ref="reference" class="n-input-address-input" :class="{ 'n-input-disabled': disabled, 'n-input-readonly': readonly }" @keydown="keyDown($event)">
          <input
            type="text"
            :placeholder="placeholder"
            v-focus="autofocus"
            :autocomplete="autocomplete"
            :tabindex="tabindex"
            :class="(clearable === true || clearable === 'hasHalue') ? 'n-input-address-clearable' : null"
            :required="required === true || (required === 'empty' && !outputValue)"
            :disabled="disabled"
            :readonly="readonly"
            @keyup.delete.stop
            :title="tooltip"
            @input="inputChanged($event)"
            @blur="$emit('blur', $event)"
            ref="input">
          <div v-if="clearable === true || (clearable === 'hasValue' && outputValue)" class="n-input-address-clear" @click="clear()">
            <slot>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m16.192 6.344-4.243 4.242-4.242-4.242-1.414 1.414 4.242 4.242-4.242 4.242 1.414 1.414 4.242-4.242 4.243 4.242 1.414-1.414-4.242-4.242 4.242-4.242z"/></svg>
            </slot>
          </div>
        </div>
      </template>
      <template #popup>
        <div class="n-input-address-options" ref="options">
          <div
            v-for="(o, i) in options"
            :key="i"
            :ref="`option-${i}`"
            class="n-input-address-option" :class="{ 'n-input-address-option-focused': i === focusedOptionIdx }"
            @mouseenter="mouseEnterOption($event, i)"
            @click="selectOption(i)"
          >
            {{ o.displayText }}
          </div>
        </div>
      </template>
    </n-popup>
  </div>
</template>

<script>
import NPopup from './npopup/NPopup.vue'
import { NPopupConfig } from './npopup/NPopupConfig'

class NInputAddressResult {
    constructor (item) {
        this.raw = item
    }
}

export class NInputAddressAccessAddress extends NInputAddressResult {
    constructor (item) {
        super(item)

        this.id = item.husnummer.id_lokalid
        this.streetName = item.husnummer.vejnavn
        this.houseNumber = item.husnummer.husnummertekst
        this.placeName = item.husnummer.supplerendebynavn.navn
        this.postalCode = parseInt(item.husnummer.postnummer.postnr)
        this.city = item.husnummer.postnummer.navn
        this.municipalityCode = parseInt(item.husnummer.navngivenvejkommunedel.kommune)
        this.utmx = item.husnummer.adgangspunkt.koordinater.x
        this.utmy = item.husnummer.adgangspunkt.koordinater.y
    }

    toString () {
        let result = `${this.streetName} ${this.houseNumber}, `

        if (this.floor) {
            result += `${this.floor}.`
            if (this.door) {
                result += ' '
            }
        }

        if (this.placeName) {
            result += `${this.placeName}, `
        }

        result += `${this.postalCode} ${this.city}`

        return result
    }
}

export class NInputAddressAddress extends NInputAddressResult {
    constructor (item) {
        super(item)

        this.id = item.adresse.husnummer.id_lokalid
        this.streetName = item.adresse.husnummer.vejnavn
        this.houseNumber = item.adresse.husnummer.husnummertekst
        this.placeName = item.adresse.husnummer.supplerendebynavn.navn
        this.postalCode = parseInt(item.adresse.husnummer.postnummer.postnr)
        this.city = item.adresse.husnummer.postnummer.navn
        this.municipalityCode = parseInt(item.adresse.husnummer.navngivenvejkommunedel.kommune)
        this.utmx = item.adresse.husnummer.adgangspunkt.koordinater.x
        this.utmy = item.adresse.husnummer.adgangspunkt.koordinater.y

        this.floor = item.adresse.etagebetegnelse
        this.door = item.adresse.doerbetegnelse
    }

    toString () {
        let result = `${this.streetName} ${this.houseNumber}, `

        if (this.floor) {
            result += `${this.floor}.`
            if (this.door) {
                result += ' '
            }
        }

        if (this.door) {
            result += this.door
        }

        if (this.floor || this.door) {
            result += ', '
        }

        if (this.placeName) {
            result += `${this.placeName}, `
        }

        result += `${this.postalCode} ${this.city}`

        return result
    }
}

export default {
    component: 'NInputAddress',
    components: {
        'n-popup': NPopup
    },
    directives: {
        focus: {
            inserted: (el, binding) => {
                if (binding.value === true) {
                    el.focus()
                } else if (typeof binding.value === 'number') {
                    setTimeout(() => {
                        el.focus()
                    }, binding.value)
                }
            }
        }
    },
    props: {
        placeholder: {
            type: String,
            default: null
        },
        autofocus: {
            type: [Boolean, Number],
            default: false
        },
        autocomplete: {
            type: [Boolean, String],
            default: null
        },
        tabindex: {
            type: Number,
            default: null
        },
        tooltip: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        required: {
            type: [Boolean, String],
            default: false,
            validator: v => typeof v === 'string' ? v === 'empty' : [true, false].includes(v)
        },
        clearable: {
            type: [Boolean, String],
            default: false,
            validator: v => typeof v === 'string' ? v === 'hasValue' : [true, false].includes(v)
        },
        municipalityCode: {
            type: [Number, Array],
            default: null
        },
        isAccessAddress: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            token: 'adressevaelger123',
            config: new NPopupConfig({
                fixed: true,
                maxWidth: null,
                mode: 'manual'
            }),
            options: [],
            focusedOptionIdx: 0,
            debounce: null,
            outputValue: null
        }
    },
    computed: {
        kommunekode () {
            if (!this.municipalityCode) {
                return null
            }

            let codes = this.municipalityCode

            if (!Array.isArray(codes)) {
                codes = [codes]
            }

            return codes.map(c => c.toString().padStart(4, '0'))
        }
    },
    created () {
        this.config.on('open', () => {
            this.$nextTick(() => {
                this.$refs.options.style.width = this.$refs.reference.offsetWidth + 'px'
            })
        })
    },
    methods: {
        clear () {
            this.$refs.input.value = null
            this.outputValue = null
            this.$emit('input', null)
            this.$emit('text', '')
        },
        focus () {
            this.$refs.input.focus()
        },
        setText (text) {
            this.$refs.input.value = text || ''
        },
        inputChanged (e) {
            if (this.$refs.input.value) {
                if (this.debounce) {
                    clearTimeout(this.debounce)
                }
                this.debounce = setTimeout(() => {
                    this.debounce = null
                    this.displayOptions({ text: this.$refs.input.value })
                }, 250)
            } else {
                this.config.close()
            }
        },
        keyDown (e) {
            if (e.altKey === false && e.ctrlKey === false && e.metaKey === false && e.code === 'ArrowUp') {
                this.focusPreviousOption()
                e.preventDefault()
            } else if (e.altKey === false && e.ctrlKey === false && e.metaKey === false && e.code === 'ArrowDown') {
                this.focusNextOption()
                e.preventDefault()
            } else if (e.altKey === false && e.ctrlKey === false && e.metaKey === false && e.code === 'Home') {
                this.focusFirstOption()
                e.preventDefault()
            } else if (e.altKey === false && e.ctrlKey === false && e.metaKey === false && e.code === 'End') {
                this.focusLastOption()
                e.preventDefault()
            } else if (e.altKey === false && e.ctrlKey === false && e.metaKey === false && e.code === 'Enter') {
                if (this.config.isOpen) {
                    this.selectOption()
                }
            }
        },
        mouseEnterOption (e, idx) {
            this.focusedOptionIdx = idx
        },
        scrollToOption () {
            this.$nextTick(() => {
                const el = this.$refs['option-' + this.focusedOptionIdx][0]
                const optionRect = el.getBoundingClientRect()
                const optionsRect = this.$refs.options.getBoundingClientRect()

                const isInViewport =
        optionRect.top >= optionsRect.top &&
        optionRect.left >= optionsRect.left &&
        optionRect.bottom <= optionsRect.bottom &&
        optionRect.right <= optionsRect.right
                if (!isInViewport) {
                    el.scrollIntoView({ block: 'nearest' })
                }
            })
        },
        focusFirstOption () {
            this.focusedOptionIdx = 0
            this.scrollToOption()
        },
        focusPreviousOption () {
            this.focusedOptionIdx--
            if (this.focusedOptionIdx === -1) {
                this.focusedOptionIdx = this.options.length - 1
            }
            this.scrollToOption()
        },
        focusNextOption () {
            this.focusedOptionIdx++
            if (this.focusedOptionIdx === this.options.length) {
                this.focusedOptionIdx = 0
            }
            this.scrollToOption()
        },
        focusLastOption () {
            this.focusedOptionIdx = this.options.length - 1
            this.scrollToOption()
        },
        selectOption (idx) {
            const optionIndex = idx !== undefined && idx !== null
                ? idx
                : this.focusedOptionIdx

            const option = this.options[optionIndex]
            if (option.type === 'vejnavn') {
                this.$refs.input.value = `${option.item.vejNavn} `
                this.displayOptions({ streetName: option.item.vejNavn })
            } else if (option.type === 'navngivenvejpostnummer') {
                this.$refs.input.value = `${option.item.vejnavn} , ${option.item.postnr} ${option.item.postdistrikt}`
                this.$refs.input.setSelectionRange(option.item.vejnavn.length + 1, option.item.vejnavn.length + 1)
                this.displayOptions({ streetName: option.item.vejnavn, zipCode: option.item.postnr })
            } else if (option.type === 'husnummer') {
                if (this.isAccessAddress) {
                    this.$refs.input.value = option.displayText
                    this.config.close()
                    this.getAddressFromId(option.id, true).then(adr => {
                        this.outputValue = new NInputAddressAccessAddress(adr)
                        this.$emit('input', this.outputValue)
                        this.$emit('text', this.outputValue.toString())
                    })
                } else {
                    this.getAddressFromId(option.id, true).then(adr => {
                        this.$refs.input.value = `${option.item.vejnavn} ${option.item.husnummer}, , ${adr.husnummer.postnummer.postnr} ${adr.husnummer.postnummer.navn}`
                        this.$refs.input.setSelectionRange(option.item.vejnavn.length + option.item.husnummer.length + 3, option.item.vejnavn.length + option.item.husnummer.length + 3)
                        this.displayOptions({ streetName: option.item.vejnavn, houseNumber: option.item.husnummer, zipCode: adr.husnummer.postnummer.postnr })
                    })
                }
            } else if (option.type === 'adresse') {
                this.$refs.input.value = option.displayText
                this.config.close()
                this.getAddressFromId(option.id, false).then(adr => {
                    this.outputValue = new NInputAddressAddress(adr)
                    this.$emit('input', this.outputValue)
                    this.$emit('text', this.outputValue.toString())
                })
            }
        },
        getAddressFromId (id, isAccessAddress) {
            const url = new URL(`https://adressevaelger.dk/${isAccessAddress ? 'husnumre' : 'adresser'}/${id}`)
            url.searchParams.append('token', this.token)
            return fetch(url).then(r => r.json()).then(r => {
                if (r.status !== 'ok') {
                    return null
                }

                return r
            })
        },
        displayOptions (params) {
            const url = new URL(`https://adressevaelger.dk/${this.isAccessAddress ? 'husnumre' : 'adresser'}/soeg`)
            if (this.kommunekode) {
                url.searchParams.append('kommunekode', this.kommunekode)
            }
            if (params.text) {
                url.searchParams.append('tekst', params.text)
            } else {
                url.searchParams.append('vejnavn', params.streetName)
                if (params.zipCode) {
                    url.searchParams.append('postnummer', params.zipCode)
                }
                if (params.houseNumber) {
                    url.searchParams.append('husnummer', params.houseNumber)
                }
            }
            url.searchParams.append('token', this.token)

            fetch(url).then(r => r.json()).then(r => {
                if (r.status !== 'ok' || r.fund.length === 0) {
                    this.config.close()
                    return
                }

                this.focusedOptionIdx = 0
                this.options = r.fund.map(i => new NAdressevaelgerApiItem(i))
                this.config.open()
                this.scrollToOption()
            })
        }
    }
}

class NAdressevaelgerApiItem {
    constructor (item) {
        this.type = item.type
        this.displayText = item.titel
        this.id = item.id
        this.item = item
    }
}
</script>

<style>
  .n-input-address {
    box-sizing: border-box;

    .n-popup {
      display: block;
    }

    .n-input-address-input {
      position: relative;
      box-sizing: border-box;

      input {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        font: inherit;
      }

      .n-input-address-clearable {
        padding-right: 1em;
      }

      .n-input-address-clear {
        position: absolute;
        right: 0.1em;
        top: 0;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        svg {
          width: 1em;
          height: 1em;
          fill: rgb(74, 74, 74);
        }

        &:hover {
          svg {
            fill: rgb(139, 139, 139);
          }
        }
      }

      &.n-input-disabled, &.n-input-readonly {
        pointer-events: none;
      }
    }

    .n-input-address-options {
      background-color: Canvas;
      z-index: 999;
      margin: 0;
      padding: 0;
      text-align: left;
      width: 100%;
      box-sizing: border-box;
      border: 1px solid ButtonBorder;
      box-shadow: 1px 0px 6px 0px rgba(0,0,0,0.5);
      max-height: 300px;
      overflow-y: auto;

      .n-input-address-option {
        box-sizing: border-box;
        padding: 1px 0.2em;

        &.n-input-address-option-focused {
          background-color: SelectedItem;
          color: SelectedItemText;
        }

        &:hover {
          cursor: default;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/shared/NInputAddress.vue